/**
 * Format bytes to human readable format
 * @param bytes - Number of bytes
 * @param decimals - Number of decimal places
 * @returns Formatted string
 */
export const formatBytes = (bytes: number, decimals: number = 2): string => {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * Format date to readable format
 * @param dateString - ISO date string or timestamp (10-digit or 13-digit)
 * @returns Formatted date string
 */
export const formatDate = (dateString: string | number): string => {
  let timestamp = dateString;
  
  // If it's a number and appears to be a 10-digit timestamp (seconds), convert to milliseconds
  if (typeof timestamp === 'number' && timestamp.toString().length === 10) {
    timestamp = timestamp * 1000;
  }
  
  const date = new Date(timestamp);
  
  if (isNaN(date.getTime())) {
    return '无效日期';
  }

  const now = new Date();
  const diffTime = date.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  // If date is in the past
  if (diffDays < 0) {
    return '已过期';
  }

  // If date is today
  if (diffDays === 0) {
    return '今天到期';
  }

  // If date is within 30 days
  if (diffDays <= 30) {
    return `${diffDays}天后到期`;
  }

  // Return formatted date
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Format currency to readable format
 * @param amount - Amount in cents
 * @returns Formatted currency string
 */
export const formatCurrency = (amount: number): string => {
  return `¥${(amount / 100).toFixed(2)}`;
};

/**
 * Format percentage
 * @param value - Percentage value (0-100)
 * @param decimals - Number of decimal places
 * @returns Formatted percentage string
 */
export const formatPercentage = (value: number, decimals: number = 1): string => {
  return `${value.toFixed(decimals)}%`;
};

/**
 * Calculate usage percentage
 * @param used - Used amount
 * @param total - Total amount
 * @returns Percentage (0-100)
 */
export const calculateUsagePercentage = (used: number, total: number): number => {
  if (total === 0) return 0;
  return Math.min((used / total) * 100, 100);
};

/**
 * Format time duration
 * @param seconds - Duration in seconds
 * @returns Formatted duration string
 */
export const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}小时${minutes}分钟`;
  } else if (minutes > 0) {
    return `${minutes}分钟${secs}秒`;
  } else {
    return `${secs}秒`;
  }
};

/**
 * Truncate text with ellipsis
 * @param text - Text to truncate
 * @param maxLength - Maximum length
 * @returns Truncated text
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};